import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Plus, Sparkles, Lock, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface QuestionsStepProps {
  onSubmit: (questions: string[]) => void;
  onBack: () => void;
  initialQuestions: string[];
  role: string;
  level: 'entry' | 'mid' | 'senior';
  skills: string[];
  isBasicPlan?: boolean;
}

const QuestionsStep: React.FC<QuestionsStepProps> = ({
  onSubmit,
  onBack,
  initialQuestions,
  role,
  level,
  skills,
  isBasicPlan = false
}) => {
  const [customQuestions, setCustomQuestions] = useState<string[]>(initialQuestions);
  const [newQuestion, setNewQuestion] = useState('');
  const [showPremiumDialog, setShowPremiumDialog] = useState(false);
  
  const handleAddQuestion = () => {
    if (!newQuestion.trim()) {
      return;
    }

    if (isBasicPlan) {
      setShowPremiumDialog(true);
      return;
    }

    setCustomQuestions([...customQuestions, newQuestion]);
    setNewQuestion('');
  };

  const handleRemoveQuestion = (index: number) => {
    const updatedQuestions = [...customQuestions];
    updatedQuestions.splice(index, 1);
    setCustomQuestions(updatedQuestions);
  };

  const handleQuickAdd = (questionText: string) => {
    if (isBasicPlan) {
      setShowPremiumDialog(true);
      return;
    }

    // If there's text in the input, add it first
    if (newQuestion.trim()) {
      setCustomQuestions([...customQuestions, newQuestion.trim()]);
    }
    
    // Directly add the new question instead of putting it in the input
    setCustomQuestions(prev => [...prev, questionText]);
    setNewQuestion('');
  };

  const handleSubmit = () => {
    // If there's a question in the input box, add it first
    if (newQuestion.trim()) {
      if (isBasicPlan) {
        setShowPremiumDialog(true);
        return;
      }
      setCustomQuestions(prev => [...prev, newQuestion.trim()]);
    }
    onSubmit(customQuestions);
  };

  return (
    <div className="bg-white/90 rounded-lg border border-[#9b87f5]/30 shadow-lg w-full max-w-3xl px-8 py-10 backdrop-blur-sm">
      <h1 className="text-3xl font-bold text-center mb-3 text-[#1A1F2C]">Assessment Questions</h1>

      <p className="text-center text-[#1A1F2C]/80 mb-8">
        Our AI will generate relevant screening questions based on the selected skills and role requirements.
      </p>

      <div className="bg-[#F8F7FF] rounded-lg p-6 mb-8 border border-[#9b87f5]/20">
        <div className="flex items-start gap-3 mb-4">
          <div className="bg-[#9b87f5]/20 p-2 rounded">
            <Sparkles size={20} className="text-[#7662DA]" />
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-1 text-[#1A1F2C]">AI-Generated Assessment</h3>
            <p className="text-[#1A1F2C]/80 text-sm mb-4">
              The AI will automatically generate a set of screening questions to assess candidates for the {level} {role} position, focusing on their experience with:
            </p>
            <div className="flex flex-wrap gap-2 mb-2">
              {skills.map((skill) => (
                <Badge key={skill} variant="skill" className="bg-[#9b87f5]/20 text-[#1A1F2C]">
                  {skill}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={`mb-8 ${isBasicPlan ? 'opacity-75' : ''}`}>
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <h3 className={`text-lg font-semibold ${isBasicPlan ? 'text-[#1A1F2C]/70' : 'text-[#1A1F2C]'}`}>
              Add Your Own Questions
            </h3>
            {isBasicPlan && (
              <Badge className="bg-orange-100 text-orange-800 border border-orange-300">
                Premium Feature
              </Badge>
            )}
          </div>
          {!isBasicPlan && (
            <Badge 
              variant="premium"
              className="bg-gradient-to-r from-[#FFB168] to-[#FF8C37] text-white"
            >
              Premium Feature
            </Badge>
          )}
        </div>
        
        <p className={`text-sm mb-4 ${isBasicPlan ? 'text-[#1A1F2C]/60' : 'text-[#1A1F2C]/80'}`}>
          Add any additional questions like salary expectations, notice period, work preferences, or other screening questions.
        </p>

        {isBasicPlan && (
          <div className="bg-orange-50 border border-orange-200 rounded-md p-4 mb-4 text-orange-800 text-sm">
            <div className="flex items-start gap-2">
              <Lock className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">This feature requires a Premium or Agency subscription.</p>
                <p className="mt-1 text-xs text-orange-700">Upgrade your plan to add unlimited custom questions to your assessments.</p>
                <Link 
                  to="/credits" 
                  className="flex items-center gap-1 mt-3 text-[#7662DA] text-sm font-medium hover:underline"
                >
                  <ExternalLink size={14} />
                  Upgrade your plan now
                </Link>
              </div>
            </div>
          </div>
        )}

        <div className={`space-y-4 mb-6 ${isBasicPlan ? 'pointer-events-none' : ''}`}>
          {customQuestions.map((question, index) => (
            <div key={index} className={`flex items-center gap-2 bg-white p-3 rounded border ${isBasicPlan ? 'border-gray-200 text-gray-400' : 'border-[#9b87f5]/30 text-[#1A1F2C]'}`}>
              <div className="flex-1">{question}</div>
              <Button 
                variant="ghost" 
                size="sm"
                className={isBasicPlan ? "text-gray-400 hover:bg-gray-100" : "text-[#1A1F2C] hover:bg-[#9b87f5]/10"}
                onClick={() => handleRemoveQuestion(index)}
                disabled={isBasicPlan}
              >
                Remove
              </Button>
            </div>
          ))}

          <div className="flex gap-2">
            <Input
              className={`${isBasicPlan 
                ? 'bg-gray-100 border-gray-200 text-gray-400 placeholder:text-gray-400 focus-visible:ring-gray-300' 
                : 'bg-white border-[#9b87f5]/30 text-[#1A1F2C] placeholder:text-[#1A1F2C]/50 focus-visible:ring-[#9b87f5]/50'}`}
              placeholder="E.g., What is your expected salary range? What is your notice period?"
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddQuestion();
                }
              }}
              disabled={isBasicPlan}
            />
            <Button 
              variant="purple"
              className={isBasicPlan 
                ? "bg-gray-300 hover:bg-gray-300 cursor-not-allowed" 
                : "bg-gradient-to-r from-[#9b87f5] to-[#7662DA] hover:from-[#8A76E5] hover:to-[#6552CA]"}
              onClick={handleAddQuestion}
              disabled={isBasicPlan}
            >
              <Plus size={16} />
            </Button>
          </div>

          <div className="mt-2">
            <p className={`text-sm ${isBasicPlan ? 'text-[#1A1F2C]/40' : 'text-[#1A1F2C]/60'} mb-2`}>Quick add:</p>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => handleQuickAdd("What is your expected salary range?")}
                className={`text-xs px-2 py-1 rounded-full ${
                  isBasicPlan 
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed" 
                    : "bg-[#F8F7FF] text-[#7662DA] hover:bg-[#9b87f5]/20 transition-colors cursor-pointer"
                }`}
                disabled={isBasicPlan}
              >
                + Salary expectations
              </button>
              <button
                onClick={() => handleQuickAdd("What is your notice period?")}
                className={`text-xs px-2 py-1 rounded-full ${
                  isBasicPlan 
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed" 
                    : "bg-[#F8F7FF] text-[#7662DA] hover:bg-[#9b87f5]/20 transition-colors cursor-pointer"
                }`}
                disabled={isBasicPlan}
              >
                + Notice period
              </button>
              <button
                onClick={() => handleQuickAdd("How many years of experience do you have?")}
                className={`text-xs px-2 py-1 rounded-full ${
                  isBasicPlan 
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed" 
                    : "bg-[#F8F7FF] text-[#7662DA] hover:bg-[#9b87f5]/20 transition-colors cursor-pointer"
                }`}
                disabled={isBasicPlan}
              >
                + Years of experience
              </button>
              <button
                onClick={() => handleQuickAdd("Are you open to working remotely?")}
                className={`text-xs px-2 py-1 rounded-full ${
                  isBasicPlan 
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed" 
                    : "bg-[#F8F7FF] text-[#7662DA] hover:bg-[#9b87f5]/20 transition-colors cursor-pointer"
                }`}
                disabled={isBasicPlan}
              >
                + Remote work
              </button>
              <button
                onClick={() => handleQuickAdd("What are your working hours preferences?")}
                className={`text-xs px-2 py-1 rounded-full ${
                  isBasicPlan 
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed" 
                    : "bg-[#F8F7FF] text-[#7662DA] hover:bg-[#9b87f5]/20 transition-colors cursor-pointer"
                }`}
                disabled={isBasicPlan}
              >
                + Working hours
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between">
        <Button 
          variant="outline"
          className="border-[#9b87f5]/30 bg-white hover:bg-[#9b87f5]/10 text-[#1A1F2C]/70 hover:text-[#1A1F2C]"
          onClick={onBack}
        >
          Back
        </Button>
        
        <Button 
          variant="purple"
          className="bg-gradient-to-r from-[#9b87f5] to-[#7662DA] hover:from-[#8A76E5] hover:to-[#6552CA] shadow-md"
          onClick={handleSubmit}
        >
          Continue
        </Button>
      </div>

      <Dialog open={showPremiumDialog} onOpenChange={setShowPremiumDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-yellow-500" />
              Premium Feature
            </DialogTitle>
            <DialogDescription>
              {isBasicPlan 
                ? "Custom questions are a premium feature available on Premium and Agency plans."
                : "Adding more than 2 custom questions is a premium feature."}
              {" "}Upgrade to add unlimited custom questions and unlock more features.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setShowPremiumDialog(false);
                setNewQuestion('');
              }}
            >
              Maybe Later
            </Button>
            <Button
              className="bg-gradient-to-r from-[#FFB168] to-[#FF8C37] text-white hover:from-[#FFA158] hover:to-[#FF7C27]"
              onClick={() => {
                setShowPremiumDialog(false);
                window.location.href = '/credits';
              }}
            >
              Upgrade Now
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuestionsStep;
